import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  MuiModal,
  WarningConfirmationModal,
} from "../../../../../../../../../../components";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import {
  EditActivityValueProps,
  ProjectActivitiesRowValue,
  ProjectCreateActivityDefaultFormKeys,
  ProjectCreateActivityDefaultProps,
  ProjectCreateActivityGeneralFormKeys,
  ProjectCreateActivityGeneralProps,
} from "../../modelsAndSchemas/models";
import { ProjectCreateActivityWrapper } from "./ProjectCreateActivity.style";
import { ProjectCreateActivityGeneral } from "./createActivityGeneral/ProjectCreateActivityGeneral";

import { useMdmSearchActivityMutation } from "../../../../../../../../../../api/services/mdmActivity/mdmActivityApis";
import { SearchMdmActivityParams } from "../../../../../../../../../../api/services/mdmActivity/models";
import { ProjectActivityPayload } from "../../../../../../../../../../api/services/project/model";
import {
  useProjectActivityMutation,
  useProjectActivityUpdateMutation,
} from "../../../../../../../../../../api/services/project/projectApis";
import { InformationBadge } from "../../../../../../../../../../common/informationBadge/informationBadge";
import { UnitTypesKey } from "../../../../../../../../../../helpers/constants/siteCoreKeys";
import {
  convertFormDataToLocalizeObj,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../../../../../helpers/utils";
import { userPreferredLanguageSelector } from "../../../../../../../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../../../redux/slices/appState/appStateSlice";
import { keyDetailsSelector } from "../../../../../../../../../../redux/slices/project/projectSelector";
import { ModalityType } from "../../../../../../../../../mdmActivitySearch/modelsAndSchemas/models";
import {
  CreateActivityDefaultValidationSchema,
  ProjectActivityGeneralValidationSchema,
} from "../../modelsAndSchemas/ValidationSchema";
import { ProjectCreateActivityDefault } from "./createActivityDefault/ProjectCreateActivityDefault";
import { addActivitySiteCoreKeys } from "../../siteCore/SiteCoreKeys";

interface ProjectCreateActivityProps {
  visible: boolean;
  onClickCloseModal: () => void;
  pageId: string;
  getListOfProjectActivitiesApi: () => void;
  isEditProjectActivity?: boolean;
  editActivityData?: EditActivityValueProps;
  isPendingActivity?: boolean;
  selectedProjectActivity?: ProjectActivitiesRowValue;
}

export const ProjectCreateActivity: FC<ProjectCreateActivityProps> = ({
  visible,
  onClickCloseModal,
  pageId,
  getListOfProjectActivitiesApi,
  isEditProjectActivity = false,
  editActivityData,
  isPendingActivity = false,
  selectedProjectActivity,
}) => {
  const dispatch = useDispatch();
  const projectKeyDetails = useSelector(keyDetailsSelector);
  const { id: projectId = "" } = projectKeyDetails || {};
  const deliveryRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );
  const userSelectedLanguage: string = useSelector(
    userPreferredLanguageSelector,
  );
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const {
    commonLabels,
    fetchLabel,
    fetchComponentTitle,
    commonValidationMessages,
    fetchDropdownValues,
  } = useSiteCore();

  const {
    mutateAsync: saveProjectActivityApi,
    isPending: isSaveProjectActivityPending,
    isError: isSaveProjectActivityError,
    isSuccess: isSaveProjectActivitySuccess,
  } = useProjectActivityMutation();

  const {
    mutateAsync: updateProjectActivityApi,
    isSuccess: isUpdateProjectActivitySuccess,
    isPending: isUpdateProjectActivityPending,
    isError: isUpdateProjectActivityError,
  } = useProjectActivityUpdateMutation();

  const {
    mutateAsync: projectGetMdmSearchActivityList,
    isPending: projectActivityTypesPending,
    data: projectActivityTypeResult,
    isError: isProjectActivityTypeError,
  } = useMdmSearchActivityMutation();

  useEffect(() => {
    if (isNotEmpty(editActivityData) && isEditProjectActivity) {
      const params = {
        activityTypeFilter: "",
        activityType: "",
        topicFilter: "",

        activityStatus: "",
        programFrameworkId: "",
        ...(isEditProjectActivity && {
          id: editActivityData.activityType.id,
        }),
      };
      projectGetMdmSearchActivityList(params as SearchMdmActivityParams);
    } else if (!isEditProjectActivity) {
      projectGetMdmSearchActivityList({} as SearchMdmActivityParams);
    }
  }, [editActivityData, isEditProjectActivity]);

  const isLoading =
    isSaveProjectActivityPending ||
    isUpdateProjectActivityPending ||
    projectActivityTypesPending;

  const isFrameworkActivity = Boolean(
    selectedProjectActivity?.projectFramework,
  );

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isLoading,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading]);

  useEffect(() => {
    if (isUpdateProjectActivitySuccess || isSaveProjectActivitySuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            addActivitySiteCoreKeys.addActivity,
            isUpdateProjectActivitySuccess
              ? addActivitySiteCoreKeys.activityUpdatedMessage
              : addActivitySiteCoreKeys.activityAddedMessage,
          ),
        }),
      );
      getListOfProjectActivitiesApi();
      onClickCloseModal();
    }
    if (
      isSaveProjectActivityError ||
      isUpdateProjectActivityError ||
      isProjectActivityTypeError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isSaveProjectActivitySuccess,
    isSaveProjectActivityError,
    isUpdateProjectActivityError,
    isUpdateProjectActivitySuccess,
  ]);

  const modalityRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );

  const defaultValuesGeneral = {
    [ProjectCreateActivityGeneralFormKeys.activityType]: "",
    [ProjectCreateActivityGeneralFormKeys.modality]: [],
    [ProjectCreateActivityGeneralFormKeys.topic]: {
      [userSelectedLanguage]: "",
    },
    [ProjectCreateActivityGeneralFormKeys.deliverables]: [],
    [ProjectCreateActivityGeneralFormKeys.activityStatus]: "",
    [ProjectCreateActivityGeneralFormKeys.triggerBilling]: false,
    [ProjectCreateActivityGeneralFormKeys.lastContactDate]: false,
    [ProjectCreateActivityGeneralFormKeys.triggerTouch]: false,
  };

  const defaultValueDefault = {
    [ProjectCreateActivityDefaultFormKeys.units]: "",
    [ProjectCreateActivityDefaultFormKeys.measurementMinute]: "",
    [ProjectCreateActivityDefaultFormKeys.measurementOther]: null,
  };

  //form for general tab
  const {
    control,
    getValues,
    setValue,
    formState: { isDirty: isDirtyGeneralForm, dirtyFields },
    reset: resetGeneralForm,
    trigger: triggerGeneralForm,
  } = useForm<ProjectCreateActivityGeneralProps>({
    mode: "all",
    defaultValues: defaultValuesGeneral,
    resolver: yupResolver(
      ProjectActivityGeneralValidationSchema(
        commonValidationMessages,
        userSelectedLanguage,
      ),
    ) as any,
  });
  //form form default tab
  const {
    watch,
    resetField: resetFieldDefaultTabValue,
    control: controlDefaultTab,
    getValues: defaultTabValues,
    formState: { isDirty: isDirtyDefaultForm },
    reset: resetDefaultForm,
    trigger: triggerDefaultForm,
  } = useForm<ProjectCreateActivityDefaultProps>({
    mode: "all",
    defaultValues: defaultValueDefault,
    resolver: yupResolver(
      CreateActivityDefaultValidationSchema(commonValidationMessages),
    ),
  });

  useEffect(() => {
    resetFieldDefaultTabValue(
      ProjectCreateActivityDefaultFormKeys.measurementOther,
    );
    resetFieldDefaultTabValue(
      ProjectCreateActivityDefaultFormKeys.measurementMinute,
    );
  }, [watch(ProjectCreateActivityDefaultFormKeys.units)]);

  useEffect(() => {
    if (
      isNotEmpty(editActivityData) &&
      isNotEmpty(projectActivityTypeResult?.data) &&
      isEditProjectActivity
    ) {
      resetGeneralForm({
        ...editActivityData,
        [ProjectCreateActivityGeneralFormKeys.activityType]:
          editActivityData.activityType?.id ?? "",
      });
      resetDefaultForm({
        ...editActivityData,
        measurementOther: editActivityData?.measurementOther ?? null,
      });

      deliveryRef.current?.resetValues(editActivityData?.deliverables ?? []);
      setTimeout(() => {
        modalityRef.current?.resetValues(editActivityData?.modality ?? []);
      });
    }
  }, [
    editActivityData,
    isEditProjectActivity,
    projectActivityTypeResult?.data,
  ]);

  const projectActivityTypeWatch = useWatch({
    control,
    name: ProjectCreateActivityGeneralFormKeys.activityType,
  });

  const onClickFinalSubmit = () => {
    const {
      activityType,
      modality: modalityIds,
      topic,
      activityStatus: activityStatusId,
      deliverables,
      triggerBilling,
      lastContactDate,
      triggerTouch,
    } = getValues();
    const { units, measurementMinute, measurementOther } = defaultTabValues();

    const measurement =
      units === UnitTypesKey.Minutes
        ? measurementMinute
        : units === UnitTypesKey.Count || units === UnitTypesKey.Days
          ? (measurementOther as number).toString()
          : "1";

    const createProjectActivityPayload: ProjectActivityPayload = {
      activityTypeId: activityType,
      modalityIds,
      localizedTopics: convertFormDataToLocalizeObj(topic),
      activityStatusId,
      deliverableIds: deliverables,
      triggerBilling,
      countsTowardsLastContactDate: lastContactDate,
      triggerTouchPointSurvey: triggerTouch,
      units,
      measurement: measurement,
      projectId: projectId,
    };
    if (isEditProjectActivity) {
      createProjectActivityPayload["id"] = selectedProjectActivity?.id ?? "";
      updateProjectActivityApi({ payload: createProjectActivityPayload });
    } else {
      saveProjectActivityApi({
        payload: createProjectActivityPayload,
        projectId,
      });
    }
  };

  const modalityListDropdownValues = fetchDropdownValues(
    addActivitySiteCoreKeys.addActivity,
    addActivitySiteCoreKeys.modalityList,
  );

  const selectedModalityList = useMemo(() => {
    if (dirtyFields?.activityType) {
      setValue(ProjectCreateActivityGeneralFormKeys.modality, [], {
        shouldValidate: false,
      });
      modalityRef.current?.resetValues([]);
    }

    if (
      !projectActivityTypeWatch ||
      !Array.isArray(projectActivityTypeResult?.data)
    ) {
      return [];
    }

    const selectedActivityType = projectActivityTypeResult?.data?.find(
      (item) => item.id === projectActivityTypeWatch,
    );
    const modalityMap = new Map(
      modalityListDropdownValues.map(({ value, label }) => [value, label]),
    );
    return (
      selectedActivityType?.modalityLst.map((v: ModalityType) => ({
        value: v.modalityName,
        label: modalityMap.get(v.modalityName) ?? v.modalityName,
      })) ?? []
    );
  }, [projectActivityTypeWatch, projectActivityTypeResult?.data]);

  const onClickConfirm = async () => {
    const defaultFormValid = await triggerDefaultForm();
    const isGeneralFormValid = await triggerGeneralForm();
    if (defaultFormValid && isGeneralFormValid) {
      onClickFinalSubmit();
    }
  };

  const closeModalHandler = () => setShowCancelConfirmation(false);

  const onClickCancelMainModal = () => {
    if (isDirtyDefaultForm || isDirtyGeneralForm)
      setShowCancelConfirmation(true);
    else onClickCloseModal();
  };

  const onClickCancelConfirm = () => {
    setShowCancelConfirmation(false);
    onClickCloseModal();
  };

  const projectActivityHeaderText = useMemo(() => {
    if (isEditProjectActivity) {
      return isFrameworkActivity
        ? fetchLabel(
            addActivitySiteCoreKeys.addActivity,
            addActivitySiteCoreKeys.ThisIsAFrameworkLevelActivity,
          )
        : fetchLabel(
            addActivitySiteCoreKeys.addActivity,
            addActivitySiteCoreKeys.ThisIsAProjectSpecificActivity,
          );
    } else {
      return fetchLabel(
        addActivitySiteCoreKeys.addActivity,
        addActivitySiteCoreKeys.projectSpecificActivityNote,
      );
    }
  }, []);

  const projectActivityTitle = useMemo(() => {
    if (isEditProjectActivity) {
      return replaceHashWithValue(
        fetchLabel(
          addActivitySiteCoreKeys.addActivity,
          addActivitySiteCoreKeys.editActivityPopupTitle,
        ),
        editActivityData?.activityType?.name ?? "",
      );
    } else {
      return fetchComponentTitle(addActivitySiteCoreKeys.addActivity);
    }
  }, [editActivityData]);

  return (
    <MuiModal
      open={visible}
      onClose={onClickCancelMainModal}
      title={projectActivityTitle}
      primaryButtonTitle={
        isEditProjectActivity ? commonLabels.update : commonLabels.save
      }
      secondaryButtonTitle={commonLabels.cancel}
      onPrimaryButtonClick={onClickConfirm}
      onSecondaryButtonClick={onClickCancelMainModal}
      hasLargeContent
    >
      <ProjectCreateActivityWrapper>
        <InformationBadge message={projectActivityHeaderText} />
        <ProjectCreateActivityGeneral
          control={control}
          pageId={pageId}
          projectDeliveryRef={deliveryRef}
          isPendingProjectActivity={isPendingActivity}
          manageLocaleTitle={projectActivityTitle}
          manageLocaleInfoMessage={projectActivityHeaderText}
          activityTypeList={projectActivityTypeResult?.data ?? []}
          modalityRef={modalityRef}
          modalityList={selectedModalityList}
          handleSaveManageLocal={(manageLocals) =>
            setValue(ProjectCreateActivityGeneralFormKeys.topic, manageLocals)
          }
          isFrameworkActivity={isFrameworkActivity}
        >
          <ProjectCreateActivityDefault
            control={controlDefaultTab}
            pageId={pageId}
            unitValue={defaultTabValues().units}
            isFrameworkActivity={isFrameworkActivity}
          />
        </ProjectCreateActivityGeneral>

        {showCancelConfirmation && (
          <WarningConfirmationModal
            show={showCancelConfirmation}
            modalTitle={commonLabels.cancelChanges}
            secondaryButtonTitle={commonLabels.no}
            onClose={closeModalHandler}
            onPrimaryButtonClick={onClickCancelConfirm}
            labelContent={commonLabels.cancelButtonAlertText}
          />
        )}
      </ProjectCreateActivityWrapper>
    </MuiModal>
  );
};
